import { useState, useEffect } from 'react';
import React from 'react';
import '@/App.css';
import '@/i18n';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Toaster } from '@/components/ui/sonner';

// Public pages
import LandingPage from '@/pages/LandingPage';

// Auth pages
import Login from '@/pages/auth/Login';
import Register from '@/pages/auth/Register';
import ForgotPassword from '@/pages/auth/ForgotPassword';
import ResetPassword from '@/pages/auth/ResetPassword';
import VerifyEmail from '@/pages/auth/VerifyEmail';
import AuthSuccess from '@/pages/auth/AuthSuccess';
import AuthCallback from '@/pages/auth/AuthCallback';

// Protected pages
import Gallery from '@/pages/Gallery';
import VideoPlayer from '@/pages/VideoPlayer';
import Map3D from '@/pages/Map3D';
import Subscriptions from '@/pages/Subscriptions';
import Dashboard from '@/pages/Dashboard';
import AdminPanel from '@/pages/AdminPanel';
import Contact from '@/pages/Contact';
import BlogPage from '@/pages/BlogPage';
import BlogPost from '@/pages/BlogPost';
import BlogEditor from '@/pages/BlogEditor';
import DemosPage from '@/pages/DemosPage';
import WhatsAppButton from '@/components/WhatsAppButton';
import { API } from '@/config';

export const AuthContext = React.createContext();

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const token = localStorage.getItem('access_token');
  
  if (!token) {
    return <Navigate to="/auth/login" replace />;
  }
  
  return children;
};

function App() {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    // Check for existing access token
    const savedToken = localStorage.getItem('access_token');
    if (savedToken) {
      setToken(savedToken);
      fetchCurrentUser(savedToken);
    } else {
      setLoading(false);
    }
  }, []);

  const fetchCurrentUser = async (accessToken) => {
    try {
      const response = await axios.get(`${API}/auth/me`, {
        headers: {
          'Authorization': `Bearer ${accessToken}`
        }
      });
      setUser(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Failed to fetch user:', error);
      localStorage.removeItem('access_token');
      setToken(null);
      setUser(null);
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      if (token) {
        await axios.post(`${API}/auth/logout`, {}, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
      }
    } catch (error) {
      console.error('Logout error:', error);
    }
    
    localStorage.removeItem('access_token');
    setUser(null);
    setToken(null);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-amber-50 to-orange-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-800 mx-auto mb-4"></div>
          <p className="text-amber-800">Cargando...</p>
        </div>
      </div>
    );
  }

  // AppRouter component to detect session_id synchronously
  const AppRouter = () => {
    const location = useLocation();

    // Send GA4 page_view on every SPA route change
    useEffect(() => {
      if (typeof window.gtag === 'function') {
        window.gtag('event', 'page_view', {
          page_path: location.pathname + location.search,
          page_location: window.location.href,
          page_title: document.title,
        });
      }
    }, [location.pathname, location.search]);

    // Check for session_id in URL hash (synchronous check during render)
    if (location.hash && location.hash.includes('session_id=')) {
      return <AuthCallback />;
    }
    
    return (
      <Routes>
        {/* Public Routes */}
        <Route path="/" element={<LandingPage />} />
        
        {/* Auth Routes */}
        <Route path="/auth/login" element={<Login />} />
        <Route path="/auth/register" element={<Register />} />
        <Route path="/auth/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/verify-email" element={<VerifyEmail />} />
        <Route path="/auth-success" element={<AuthSuccess />} />
        
        {/* Protected Routes */}
        <Route path="/gallery" element={<Gallery />} />
        <Route path="/map3d" element={<Map3D />} />
        <Route path="/suscripciones" element={<Subscriptions />} />
        <Route path="/video/:id" element={<VideoPlayer />} />
        <Route 
          path="/contact" 
          element={<Contact />} 
        />
        <Route 
          path="/dashboard" 
          element={
            <ProtectedRoute>
              <Dashboard />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/admin" 
          element={
            <ProtectedRoute>
              <AdminPanel />
            </ProtectedRoute>
          } 
        />

        {/* Blog Routes */}
        <Route path="/blog" element={<BlogPage />} />

        {/* Demos (public) */}
        <Route path="/demos" element={<DemosPage />} />
        <Route 
          path="/blog/new" 
          element={
            <ProtectedRoute>
              <BlogEditor />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/blog/edit/:id" 
          element={
            <ProtectedRoute>
              <BlogEditor />
            </ProtectedRoute>
          } 
        />
        <Route path="/blog/:id" element={<BlogPost />} />

        {/* Catch all - redirect to home */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    );
  };

  return (
    <AuthContext.Provider value={{ user, token, setUser, setToken, logout, fetchCurrentUser }}>
      <div className="App">
        <BrowserRouter>
          <AppRouter />
          <WhatsAppButton />
        </BrowserRouter>
        <Toaster position="top-right" />
      </div>
    </AuthContext.Provider>
  );
}

export default App;
